import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ArrowForwardOutlined, CloseOutlined, SearchOutlined } from '@mui/icons-material';
import { Box, Button, ButtonBase, CircularProgress, ClickAwayListener, Dialog, IconButton, InputBase, Paper, Popper, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { visuallyHiddenStyles } from './visuallyHidden';
import { createFocusRingStyles, designLayers } from '../interaction';
import { createInputSurfaceStyles } from '../inputSurface';
import { designTokens } from '../tokens';

export type GlobalSearchItem = {
  id: string;
  label: string;
  description?: string;
  leading?: ReactNode;
};

export type GlobalSearchGroup = {
  id: string;
  label: string;
  items: readonly GlobalSearchItem[];
  moreLabel?: string;
  onMore?: () => void;
};

export type GlobalSearchProps = {
  label: string;
  placeholder?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
  groups: readonly GlobalSearchGroup[];
  /** false를 반환하면 검색어·선택·초점을 유지합니다. */
  onSelect: (id: string) => boolean | void;
  /** 숨은 작업공간과 검색을 제공하지 않는 세션에서는 단축키도 차단합니다. */
  enabled?: boolean;
  loading?: boolean;
  /** 검색 요청, 부분 오류·재시도, 빈 결과 안내는 호출자가 소유합니다. */
  status?: ReactNode;
  liveMessage?: string;
};

function emphasizeMatches(text: string, pattern: RegExp | undefined): ReactNode {
  if (!pattern) return text;
  const parts: ReactNode[] = [];
  let offset = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index > offset) parts.push(text.slice(offset, match.index));
    parts.push(<Box component="span" key={match.index} sx={{ fontWeight: 600 }}>{match[0]}</Box>);
    offset = match.index + match[0].length;
  }
  if (offset === 0) return text;
  if (offset < text.length) parts.push(text.slice(offset));
  return parts;
}

/** 검색 대상·권한·요청·이동을 모르는 헤더 검색 표면입니다. */
export function GlobalSearch({ label, placeholder = '검색', open, onOpenChange, query, onQueryChange, groups, onSelect, enabled = true, loading = false, status, liveMessage }: GlobalSearchProps) {
  const mobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const id = useId();
  const resultsId = `${id}-results`;
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [anchorWidth, setAnchorWidth] = useState(0);
  const composingRef = useRef(false);
  const suppressNextFocus = useRef(false);
  const [compositionQuery, setCompositionQuery] = useState<string>();
  const [activeId, setActiveId] = useState<string>();
  const [suppressRestoreFocus, setSuppressRestoreFocus] = useState(false);
  const items = groups.flatMap((group) => group.items);
  const activeItem = items.find((item) => item.id === activeId) ?? items[0];
  const activeIndex = activeItem ? items.indexOf(activeItem) : -1;
  const expanded = open && enabled;
  const optionId = (item: GlobalSearchItem) => `${id}-option-${encodeURIComponent(item.id)}`;
  const focusStyle = { '&.Mui-focusVisible, &:focus-visible': createFocusRingStyles({ inset: true }) };
  const matchPattern = useMemo(() => {
    // 조회용 정규화와 분리해 원문 위치를 보존하고 입력을 정규식 문법으로 해석하지 않습니다.
    const words = [...new Set(query.trim().split(/\s+/).filter(Boolean))].sort((left, right) => right.length - left.length);
    return words.length ? new RegExp(words.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'giu') : undefined;
  }, [query]);

  const close = (restoreFocus = false) => {
    onOpenChange(false);
    if (restoreFocus && !mobile && document.activeElement !== inputRef.current) {
      suppressNextFocus.current = true;
      inputRef.current?.focus({ preventScroll: true });
    }
  };
  const select = (itemId: string) => {
    if (onSelect(itemId) === false) return;
    setSuppressRestoreFocus(true);
    onOpenChange(false);
  };

  useEffect(() => {
    const anchor = rootRef.current;
    if (!anchor) return;
    const observer = new ResizeObserver(() => setAnchorWidth(anchor.clientWidth));
    observer.observe(anchor);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!expanded) return;
    setSuppressRestoreFocus(false);
    // 화면 폭 전환과 모바일 Dialog의 mount 뒤에도 검색 입력을 이어갑니다.
    const frame = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [expanded, mobile]);

  useEffect(() => {
    if (!enabled && open) onOpenChange(false);
  }, [enabled, open, onOpenChange]);

  useEffect(() => {
    if (!enabled) return;
    const handleShortcut = (event: globalThis.KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== 'k' || composingRef.current || event.isComposing || event.repeat) return;
      const hasOtherModal = [...document.querySelectorAll<HTMLElement>('.MuiModal-root, [role="dialog"][aria-modal="true"]')]
        .some((modal) => modal.getAttribute('aria-hidden') !== 'true' && modal.getClientRects().length > 0 && !dialogRef.current?.contains(modal));
      if (hasOtherModal || rootRef.current?.closest('[inert]')) return;
      event.preventDefault();
      onOpenChange(true);
      inputRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [enabled, onOpenChange]);

  useEffect(() => {
    if (!expanded || mobile) return;
    const handleFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) onOpenChange(false);
    };
    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, [expanded, mobile, onOpenChange]);

  useEffect(() => {
    if (!expanded || !activeItem) return;
    document.getElementById(optionId(activeItem))?.scrollIntoView({ block: 'nearest' });
  }, [expanded, activeItem?.id]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (composingRef.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === 'Escape' && expanded) {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!expanded) onOpenChange(true);
      if (items.length > 0) {
        const nextIndex = event.key === 'ArrowDown'
          ? (!expanded ? 0 : (activeIndex + 1) % items.length)
          : (!expanded ? items.length - 1 : (activeIndex - 1 + items.length) % items.length);
        setActiveId(items[nextIndex].id);
      }
    } else if (event.key === 'Enter' && expanded && items.length > 0) {
      event.preventDefault();
      select((activeItem ?? items[0]).id);
    }
  };

  const input = <InputBase
    inputRef={inputRef}
    value={compositionQuery ?? query}
    disabled={!enabled}
    placeholder={placeholder}
    onFocus={() => { if (suppressNextFocus.current) suppressNextFocus.current = false; else onOpenChange(true); }}
    onCompositionStart={() => { composingRef.current = true; }}
    onCompositionEnd={(event) => {
      composingRef.current = false;
      setCompositionQuery(undefined);
      setActiveId(undefined);
      onQueryChange((event.target as HTMLInputElement).value);
    }}
    onChange={(event) => {
      if (composingRef.current) setCompositionQuery(event.target.value);
      else { setActiveId(undefined); onQueryChange(event.target.value); }
      onOpenChange(true);
    }}
    onKeyDown={handleKeyDown}
    startAdornment={<SearchOutlined aria-hidden sx={{ ml: 1.5, mr: 1, color: designTokens.color.secondaryText, fontSize: designTokens.layout.menuItemIconSize }} />}
    endAdornment={<Stack direction="row" sx={{ alignItems: 'center', pr: 0.5 }}>
      {loading && <CircularProgress aria-label="검색 중" size={designTokens.layout.searchIconSize} sx={{ mx: 1 }} />}
      {query ? <IconButton aria-label="검색어 지우기" size="small" onMouseDown={(event) => event.preventDefault()} onClick={() => { composingRef.current = false; setCompositionQuery(undefined); setActiveId(undefined); onQueryChange(''); inputRef.current?.focus(); }} sx={focusStyle}><CloseOutlined sx={{ fontSize: designTokens.layout.menuItemIconSize }} /></IconButton>
        : !mobile && <Box component="kbd" aria-hidden sx={{ whiteSpace: 'nowrap', mx: 1, color: designTokens.color.mutedText, ...designTokens.typography.supporting }}>{typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘ K' : 'Ctrl K'}</Box>}
    </Stack>}
    inputProps={{
      role: 'combobox',
      'aria-label': label,
      'aria-expanded': expanded,
      'aria-autocomplete': 'list',
      'aria-controls': expanded ? resultsId : undefined,
      'aria-activedescendant': expanded && activeItem ? optionId(activeItem) : undefined,
      autoComplete: 'off',
      enterKeyHint: 'search',
    }}
    sx={{
      ...createInputSurfaceStyles(),
      width: '100%', minWidth: 0,
      height: designTokens.layout.controlMinHeight,
      bgcolor: expanded ? designTokens.color.canvas : designTokens.color.neutralRaisedSurface,
      ...designTokens.typography.ui,
      '& input': { minWidth: 0, py: 1, '&::placeholder': { color: designTokens.color.mutedText, opacity: 1 } },
      [designTokens.media.touchTarget]: { height: designTokens.layout.touchTargetMinSize, fontSize: designTokens.typography.body.fontSize },
    }}
  />;

  const results = <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
    <Box sx={{ overflowY: 'auto', overscrollBehavior: 'contain', minHeight: 0, p: 1, flex: mobile ? 1 : undefined }}>
      <Box id={resultsId} role="listbox" aria-label={`${label} 결과`} aria-busy={loading}>
        {groups.filter((group) => group.items.length > 0).map((group) => <Box key={group.id} role="group" aria-labelledby={`${id}-group-${group.id}`} sx={{ '& + &': { mt: 1 } }}>
          <Typography id={`${id}-group-${group.id}`} component="div" sx={{ px: 1.5, py: 1, color: designTokens.color.secondaryText, ...designTokens.typography.supporting, fontWeight: 600 }}>{group.label}</Typography>
          {group.items.map((item) => <ButtonBase
            component="div"
            key={item.id}
            id={optionId(item)}
            role="option"
            aria-label={item.description ? `${item.label} ${item.description}` : item.label}
            aria-selected={item.id === activeItem?.id}
            tabIndex={-1}
            disableRipple
            onMouseDown={(event) => event.preventDefault()}
            onMouseMove={() => setActiveId(item.id)}
            onClick={() => select(item.id)}
            sx={{
              display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', gap: 1.5, px: 1.5, py: 1,
              minHeight: designTokens.layout.listRowCompactMinHeight, borderRadius: designTokens.radius.menuItem,
              bgcolor: item.id === activeItem?.id ? designTokens.color.selectedNavigationSurface : 'transparent',
              '&:hover': { bgcolor: designTokens.color.hoverSurface },
              '@media (forced-colors: active)': { outline: item.id === activeItem?.id ? '2px solid Highlight' : undefined, outlineOffset: -2 },
              ...focusStyle,
            }}
          >
            {item.leading && <Box aria-hidden sx={{ display: 'grid', placeItems: 'center', flexShrink: 0, color: designTokens.color.secondaryText, '& > .MuiSvgIcon-root': { fontSize: designTokens.layout.menuItemIconSize } }}>{item.leading}</Box>}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography component="div" sx={{ ...designTokens.typography.ui, fontWeight: 500, overflowWrap: 'anywhere' }}>{emphasizeMatches(item.label, matchPattern)}</Typography>
              {item.description && <Typography component="div" sx={{ ...designTokens.typography.supporting, color: designTokens.color.secondaryText, overflowWrap: 'anywhere' }}>{emphasizeMatches(item.description, matchPattern)}</Typography>}
            </Box>
            <ArrowForwardOutlined aria-hidden sx={{ flexShrink: 0, color: designTokens.color.mutedText, fontSize: designTokens.layout.searchIconSize }} />
          </ButtonBase>)}
        </Box>)}
      </Box>
      {status != null && <Box sx={{ p: 1.5 }}>{status}</Box>}
      {groups.some((group) => group.moreLabel && group.onMore) && <Stack direction="row" useFlexGap sx={{ gap: 1, flexWrap: 'wrap', px: 1, pt: 1, mt: 1, borderTop: '1px solid', borderColor: designTokens.color.divider }}>
        {groups.filter((group) => group.moreLabel && group.onMore).map((group) => <Button key={group.id} size="small" variant="text" endIcon={<ArrowForwardOutlined />} onClick={() => group.onMore?.()}>{group.moreLabel}</Button>)}
      </Stack>}
    </Box>
    {liveMessage && <Box role="status" aria-live="polite" sx={visuallyHiddenStyles}>{liveMessage}</Box>}
  </Box>;

  return <ClickAwayListener onClickAway={() => { if (expanded && !mobile) close(); }}>
    <Box ref={rootRef} data-testid="global-search" onKeyDown={(event) => {
      if (event.key === 'Escape' && expanded && !composingRef.current && !event.nativeEvent.isComposing && event.keyCode !== 229) {
        event.preventDefault();
        event.stopPropagation();
        close(true);
      }
    }} sx={{ minWidth: 0 }}>
      {mobile
        ? <Tooltip title={label}><IconButton aria-label={label} aria-haspopup="dialog" aria-expanded={expanded} disabled={!enabled} onClick={() => onOpenChange(true)} sx={focusStyle}><SearchOutlined /></IconButton></Tooltip>
        : input}
      {!mobile && <Popper open={expanded} anchorEl={rootRef.current} placement="bottom-start" disablePortal modifiers={[{ name: 'offset', options: { offset: [0, designTokens.layout.popupAnchorGap] } }]} sx={{ width: anchorWidth, zIndex: designLayers.workspaceHeader }}>
        <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', maxHeight: `min(${designTokens.layout.globalSearchPanelMaxHeight}px, calc(100dvh - ${designTokens.layout.workspaceHeaderHeight + designTokens.space.md}px))`, overflow: 'hidden', border: '1px solid', borderColor: designTokens.color.divider, borderRadius: designTokens.radius.popover, boxShadow: designTokens.shadow.menu }}>{results}</Paper>
      </Popper>}
      {mobile && <Dialog ref={dialogRef} open={expanded} fullScreen onClose={(_, reason) => { if (reason !== 'escapeKeyDown' || !composingRef.current) close(true); }} disableRestoreFocus={suppressRestoreFocus} slotProps={{ paper: { 'aria-label': label, sx: { bgcolor: designTokens.color.canvas } } }}>
        <Stack direction="row" sx={{ p: 1.5, gap: 1, alignItems: 'center', borderBottom: '1px solid', borderColor: designTokens.color.divider }}>
          {input}
          <IconButton aria-label="검색 닫기" onClick={() => close(true)} sx={focusStyle}><CloseOutlined /></IconButton>
        </Stack>
        {results}
      </Dialog>}
    </Box>
  </ClickAwayListener>;
}
